<script setup lang="ts">
import type { RouteLocation } from "#vue-router";
import OverviewAuthorsList from "~/components/templates/v1/RTOverview/OverviewAuthorsList/OverviewAuthorsList.vue";
import RTOverviewBackground from "~/components/templates/v1/RTOverview/RTOverviewBackground/RTOverviewBackground.vue";
import { CardArticle } from "@frontiers-vue3/public-pages";
import type { IArticle } from "~/types";
import { scrollToItem } from "~/helpers";
import { useEnvironmentConfigStore } from "~/stores/environmentConfig";
import { $frontiersClibConfig, Spinner } from "@frontiers-vue3/public-pages";
import { isoToDateConverter } from "~/helpers";
import { SearchDataModel } from "~/types/models/SearchData.model";
import { ArticleSortOptionEnum } from "~/enums/ArticleSortOptionEnum";

const mainStore = useMain();
const route = useRoute();
const terminology = useTerminologyStore();
const runtimeConfig = useRuntimeConfig();
const { isTopicCoordinatorVisible, isTopicEditorsVisible } =
  runtimeConfig.public;
const { isImpactMetricsVisible } = useEnvironmentConfigStore().getConfig();
const isFrontiers = $frontiersClibConfig.getIsFrontiers;

const metaData = usePageMetaInfo().getResearchTopicDetailsPageMetaInfo(
  mainStore.currentResearchTopic
);

useHead(() => metaData);

const viewArticlesLink = computed((): string | RouteLocation => {
  return !route.params.rtTabs
    ? `${route.path}/articles`
    : {
        ...route,
        params: {
          ...route.params,
          rtTabs: "articles"
        }
      };
});

const displayTopicEditors = computed((): boolean => {
  return (
    Boolean(isTopicEditorsVisible) &&
    !!mainStore.currentResearchTopic.topicEditors.length
  );
});

const displayTopicCoordinator = computed((): boolean => {
  return (
    Boolean(isTopicCoordinatorVisible) &&
    !!mainStore.currentResearchTopic.topicCoordinators.length
  );
});

const isImpactViewsVisible = ref<boolean>(isImpactMetricsVisible);

const formatDateForArticleCard = (article: IArticle): string => {
  return article.publishedOn ? isoToDateConverter(article.publishedOn) : "";
};

const scrollToMainTabs = () => {
  //This setTimeout is used to wait for the iBar to reset correctly before scrolling
  setTimeout(() => scrollToItem("MainTabs"), 400);
};

const { data } = useAsyncData("overview", async () => {
  const { getArticles } = useProxyService();
  const overviewArticles = await getArticles(
    Number(route.params.rtId),
    new SearchDataModel(0, 3, ArticleSortOptionEnum.DATE)
  );

  return { overviewArticles: overviewArticles.articles };
});
</script>

<template>
  <div class="RTOverview">
    <RTOverviewBackground :research-topic="mainStore.currentResearchTopic" />

    <!-- Topic Editors -->
    <OverviewAuthorsList
      v-if="displayTopicEditors"
      topic-title="editors"
      :topic-editors-or-coordinators="
        mainStore.currentResearchTopic.topicEditors
      " />

    <!-- Topic coordinators -->
    <OverviewAuthorsList
      v-if="displayTopicCoordinator"
      topic-title="coordinators"
      :topic-editors-or-coordinators="
        mainStore.currentResearchTopic.topicCoordinators
      " />

    <!-- Articles -->
    <section v-if="mainStore.totalArticles" class="Articles">
      <div class="Articles__Header">
        <h2 class="Articles__Header__Title h2">
          {{ terminology.getTerm("articles") }}
        </h2>

        <NuxtLink
          :to="viewArticlesLink"
          class="Articles__Header__Link"
          @click="scrollToMainTabs">
          <span>
            See all
            {{ terminology.getTerm("articles").toLowerCase() }}
            ({{ mainStore.totalArticles }})</span
          >
        </NuxtLink>
      </div>
      <Spinner
        v-if="!data?.overviewArticles?.length"
        class="Articles__Header__Spinner"
        size="2xl"
        display="inline-flex" />

      <div v-else class="RTOverview__Articles--container">
        <CardArticle
          v-for="article in data?.overviewArticles"
          :id="article.id.toString()"
          :key="article.id"
          :doi="article.doi"
          :type="article.articleType?.toUpperCase() || ''"
          :accepted-date="article.acceptedDate"
          :published-date="formatDateForArticleCard(article)"
          :title="article.title"
          :authors="article.authors ?? []"
          :total-views="isImpactViewsVisible ? article.impactV5?.views || 0 : 0"
          :total-citations="article.impactV5?.citations || 0"
          :image-url="article.imageUrl"
          journal-name=""
          :journal-slug="article.journal?.journalSlug || ''"
          :use-new-url="true" />
      </div>
    </section>
  </div>
</template>

<style lang="scss">
:root {
  --descriptionHeight: 120px;
}

.RTOverview {
  --main-text-img-size: 202px;
  width: var(--layout-col-12);

  @media #{$break-laptop} {
    --main-text-img-size: 142px;
  }

  @media #{$break-laptop-sm} {
    width: var(--layout-col-14);
    --main-text-img-size: 124px;
  }

  @media #{$break-md} {
    --main-text-img-size: 110px;
  }

  @media #{$break-sm} {
    --main-text-img-size: 124px;
  }

  @media #{$break-md} {
    width: 100%;
  }

  &__Articles--container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 32px;
    grid-row-gap: 32px;
    margin-top: 32px;
    margin-bottom: 56px;

    a {
      display: inherit;
    }

    @media #{$break-laptop} {
      max-width: none;
      grid-template-columns: repeat(2, 1fr);
    }

    @media #{$break-xs} {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  .Articles {
    &__Header {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      height: fit-content;
      margin-top: var(--spacing-vertical-xl);

      &__Title {
        margin-top: 0;
      }

      &__Link {
        @include f-16;
        font-weight: 600;
        bottom: -10px;
        right: 0;
        padding-left: 16px;
        color: var(--maincolor50);
        background-color: $grey10;
        font-family: "MuseoSans";
        transition: color 300ms $bezier;
        margin-top: 0;

        &:hover {
          color: var(--maincolor70);
          span {
            text-decoration: underline;
            text-decoration-thickness: 1px;
            text-underline-offset: 2px;
          }
        }

        &::after {
          font-family: $fontIcon;
          content: "\e90e";
          font-size: 16px;
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
